import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PaperClipIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import supportService from '../services/supportService';
import type { SupportTicket, TicketComment } from '../services/supportService';

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/html',
  'application/json',
  'application/xml',
  'application/zip',
  'application/gzip',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.ms-excel',
  'video/mp4',
  'video/webm',
  'audio/mpeg',
  'audio/wav',
];

const STATUS_LABELS: Record<string, string> = {
  new: 'Neu',
  open: 'Offen',
  in_progress: 'In Bearbeitung',
  waiting: 'Wartend',
  resolved: 'Erledigt',
  closed: 'Geschlossen',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: string }) {
  const label = STATUS_LABELS[status] || status;
  let classes = '';
  switch (status) {
    case 'new':
      classes = 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
      break;
    case 'open':
      classes = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      break;
    case 'in_progress':
      classes = 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      break;
    case 'resolved':
      classes = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      break;
    case 'closed':
      classes = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      break;
    default:
      classes = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}>
      {label}
    </span>
  );
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ isOpen, onClose }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'create' | 'tickets'>('create');

  // Form state (Tab 1)
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [successHadAttachment, setSuccessHadAttachment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tickets state (Tab 2)
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentSending, setCommentSending] = useState(false);

  const ticketsLoadedRef = useRef(false);

  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const response = await supportService.listMyTickets();
      setTickets(response.items);
    } catch {
      // Silently handle -- tickets tab will show empty
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  const loadComments = useCallback(async (ticketNumber: string) => {
    setCommentsLoading(true);
    try {
      const allComments = await supportService.getTicketComments(ticketNumber);
      setComments(allComments.filter((c) => !c.is_internal));
    } catch {
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setSelectedFile(null);
      setFileError(null);
      setSuccess(null);
      setSuccessHadAttachment(false);
      setError(null);
      setActiveTab('create');
      setSelectedTicket(null);
      setComments([]);
      setNewComment('');
      ticketsLoadedRef.current = false;
    }
  }, [isOpen]);

  // Load tickets on first switch to tickets tab
  useEffect(() => {
    if (activeTab === 'tickets' && !ticketsLoadedRef.current) {
      ticketsLoadedRef.current = true;
      loadTickets();
    }
  }, [activeTab, loadTickets]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const file = e.target.files?.[0] || null;

    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError(`Datei ist zu gro\u00df (${formatFileSize(file.size)}). Maximal 50 MB.`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError('Dateityp nicht erlaubt. Erlaubt: Bilder, PDF, Textdateien.');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Bitte geben Sie einen Titel ein.');
      return;
    }

    try {
      setLoading(true);
      const ticket = await supportService.createTicket({
        title: title.trim(),
        description: description.trim() || undefined,
      });

      const hadAttachment = !!selectedFile;

      if (selectedFile) {
        try {
          await supportService.uploadAttachment(ticket.ticket_number, selectedFile);
        } catch {
          setSuccess(ticket.ticket_number);
          setSuccessHadAttachment(false);
          setError('Ticket wurde erstellt, aber der Anhang konnte nicht hochgeladen werden.');
          return;
        }
      }

      // After successful creation, reload tickets and switch to detail view
      ticketsLoadedRef.current = false;
      const response = await supportService.listMyTickets();
      setTickets(response.items);
      ticketsLoadedRef.current = true;

      // Find the newly created ticket and show it
      const createdTicket = response.items.find((t) => t.ticket_number === ticket.ticket_number);
      if (createdTicket) {
        setSelectedTicket(createdTicket);
        loadComments(createdTicket.ticket_number);
      }

      setSuccess(ticket.ticket_number);
      setSuccessHadAttachment(hadAttachment);
      setActiveTab('tickets');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string; detail?: string } } };
      const message =
        axiosErr.response?.data?.error ||
        axiosErr.response?.data?.detail ||
        'Fehler beim Erstellen des Tickets. Bitte versuchen Sie es erneut.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTicket = async (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setNewComment('');
    loadComments(ticket.ticket_number);
  };

  const handleBackToList = () => {
    setSelectedTicket(null);
    setComments([]);
    setNewComment('');
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    setCommentSending(true);
    try {
      await supportService.addComment(selectedTicket.ticket_number, newComment.trim());
      setNewComment('');
      loadComments(selectedTicket.ticket_number);
    } catch {
      // Silently handle
    } finally {
      setCommentSending(false);
    }
  };

  const renderCreateTab = () => {
    if (success) {
      return (
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg
              className="w-6 h-6 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p className="text-primary dark:text-white font-medium">
            {successHadAttachment
              ? `Vielen Dank! Ihr Ticket (${success}) mit Anhang wurde erstellt.`
              : `Vielen Dank! Ihr Ticket (${success}) wurde erstellt.`}
          </p>
          {error && (
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
              {error}
            </p>
          )}
          <p className="text-sm text-muted mt-2">
            Wir werden uns so schnell wie m\u00f6glich darum k\u00fcmmern.
          </p>
          <button
            onClick={() => setActiveTab('tickets')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
          >
            Ticket anzeigen
          </button>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-4 p-4">
        {/* Titel */}
        <div>
          <label className="block text-sm font-medium text-primary dark:text-white mb-1">
            Titel <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Kurze Beschreibung des Problems"
            className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-accent focus:border-accent dark:bg-gray-700 dark:text-white dark:border-gray-600 placeholder-muted"
            required
            autoFocus
          />
        </div>

        {/* Beschreibung */}
        <div>
          <label className="block text-sm font-medium text-primary dark:text-white mb-1">
            Beschreibung
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Detaillierte Beschreibung (optional)"
            rows={4}
            className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-accent focus:border-accent dark:bg-gray-700 dark:text-white dark:border-gray-600 placeholder-muted resize-none"
          />
        </div>

        {/* Anhang */}
        <div>
          <label className="block text-sm font-medium text-primary dark:text-white mb-1">
            Anhang
          </label>
          {selectedFile ? (
            <div className="flex items-center gap-3 px-4 py-2.5 border border-[rgba(30,77,107,0.2)] rounded-xl dark:border-gray-600 bg-warm-50 dark:bg-gray-700/50">
              <PaperClipIcon className="w-4 h-4 text-muted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-primary dark:text-white truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-muted">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-1 text-muted hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                aria-label="Datei entfernen"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv"
              className="w-full text-sm text-muted file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-warm-100 file:text-primary dark:file:bg-gray-700 dark:file:text-white hover:file:bg-warm-200 dark:hover:file:bg-gray-600 file:cursor-pointer file:transition-colors"
            />
          )}
          {fileError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {fileError}
            </p>
          )}
          <p className="mt-1 text-xs text-muted">
            Bilder, PDF oder Textdateien. Maximal 50 MB.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl text-primary dark:text-white hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Wird gesendet...' : 'Absenden'}
          </button>
        </div>
      </form>
    );
  };

  const renderTicketDetail = () => {
    if (!selectedTicket) return null;

    return (
      <div className="p-4 space-y-4">
        {/* Back button */}
        <button
          onClick={handleBackToList}
          className="flex items-center gap-1.5 text-sm text-muted hover:text-primary dark:hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Zur\u00fcck zur Liste
        </button>

        {/* Ticket info */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted font-mono">{selectedTicket.ticket_number}</span>
            <StatusBadge status={selectedTicket.status} />
          </div>
          <h3 className="text-lg font-semibold text-primary dark:text-white">
            {selectedTicket.title}
          </h3>
          {selectedTicket.description && (
            <p className="text-sm text-muted whitespace-pre-wrap">
              {selectedTicket.description}
            </p>
          )}
          <p className="text-xs text-muted">
            Erstellt am {formatDate(selectedTicket.created_at)}
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-[rgba(30,77,107,0.1)] dark:border-gray-600" />

        {/* Comments */}
        <div>
          <h4 className="text-sm font-medium text-primary dark:text-white mb-3">
            Kommentare
          </h4>
          {commentsLoading ? (
            <p className="text-sm text-muted">Wird geladen...</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-muted">Noch keine Kommentare.</p>
          ) : (
            <div className="space-y-3">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="p-3 rounded-lg bg-warm-50 dark:bg-gray-700/50 border border-[rgba(30,77,107,0.1)] dark:border-gray-600"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-primary dark:text-white">
                      {comment.author_name}
                    </span>
                    <span className="text-xs text-muted">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-primary dark:text-gray-200 whitespace-pre-wrap">
                    {comment.content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add comment */}
        {selectedTicket.status !== 'closed' && (
          <div className="space-y-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Kommentar hinzuf\u00fcgen..."
              rows={3}
              className="w-full px-4 py-2 border border-[rgba(30,77,107,0.2)] rounded-xl focus:ring-2 focus:ring-accent focus:border-accent dark:bg-gray-700 dark:text-white dark:border-gray-600 placeholder-muted resize-none"
            />
            <div className="flex justify-end">
              <button
                onClick={handleAddComment}
                disabled={commentSending || !newComment.trim()}
                className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 disabled:opacity-50 transition-colors text-sm"
              >
                {commentSending ? 'Wird gesendet...' : 'Senden'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTicketsList = () => {
    if (ticketsLoading) {
      return (
        <div className="p-4">
          <p className="text-sm text-muted">Wird geladen...</p>
        </div>
      );
    }

    if (tickets.length === 0) {
      return (
        <div className="p-4">
          <p className="text-sm text-muted">Sie haben noch keine Tickets erstellt.</p>
        </div>
      );
    }

    return (
      <div className="divide-y divide-[rgba(30,77,107,0.1)] dark:divide-gray-600">
        {tickets.map((ticket) => (
          <button
            key={ticket.id}
            onClick={() => handleSelectTicket(ticket)}
            className="w-full text-left px-4 py-3 hover:bg-warm-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted font-mono">{ticket.ticket_number}</span>
              <StatusBadge status={ticket.status} />
            </div>
            <p className="text-sm font-medium text-primary dark:text-white truncate">
              {ticket.title}
            </p>
            <p className="text-xs text-muted mt-0.5">
              {formatDate(ticket.created_at)}
            </p>
          </button>
        ))}
      </div>
    );
  };

  const renderTicketsTab = () => {
    if (selectedTicket) {
      return renderTicketDetail();
    }
    return renderTicketsList();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h2 className="text-xl font-bold text-primary dark:text-white">
            Support
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-muted hover:text-primary dark:hover:text-white hover:bg-warm-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Schlie\u00dfen"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-[rgba(30,77,107,0.1)] dark:border-gray-600 px-6">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-2.5 text-sm transition-colors ${
              activeTab === 'create'
                ? 'border-b-2 border-accent text-accent font-medium'
                : 'text-muted hover:text-primary dark:hover:text-white'
            }`}
          >
            Neues Ticket
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`px-4 py-2.5 text-sm transition-colors ${
              activeTab === 'tickets'
                ? 'border-b-2 border-accent text-accent font-medium'
                : 'text-muted hover:text-primary dark:hover:text-white'
            }`}
          >
            Meine Tickets{tickets.length > 0 && ` (${tickets.length})`}
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'create' ? renderCreateTab() : renderTicketsTab()}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FeedbackDialog;
